import type { DomainPathRule } from '@terra/shared-schemas-orchestrator';
import {
  isHostRestrictingAllowlistRule,
  resolveExclusiveDomainScopeFlags,
  scopeFlagsFromMode,
  shouldAutoMarkThirdParty,
  splitDomainHostAndPath,
  type DomainScopeFlags,
} from '@terra/shared-asset-processing';
import { isValidScopePath } from './domain-validation';

export { isValidScopePath };

export type DomainConfigScopeSyncInput = {
  domain: string;
};

export type DomainPathRuleInput = {
  path: string;
  scope: DomainPathRule['scope'];
  isInFunctionalityScope?: boolean;
  isInTestingScope?: boolean;
  isThirdParty?: boolean;
};

export type DomainConfigNormalizeInput = DomainConfigScopeSyncInput & {
  isInFunctionalityScope?: boolean | null;
  isInTestingScope?: boolean | null;
  isThirdParty?: boolean | null;
  pathRules?: DomainPathRuleInput[];
};

export type NormalizeStoredDomainConfigOptions = {
  primaryHost: string;
};

const hostWithoutWww = (host: string): string => {
  const normalized = host.toLowerCase();

  return normalized.startsWith('www.') ? normalized.slice(4) : normalized;
};

const isApplicationPrimaryHost = ({
  host,
  primaryHost,
}: {
  host: string;
  primaryHost?: string | null;
}): boolean => {
  if (!primaryHost) {
    return false;
  }

  const { host: bareHost } = splitDomainHostAndPath(host);

  return hostWithoutWww(bareHost) === hostWithoutWww(primaryHost);
};

export function resolveAddDomainInitialScopeFlags({
  host,
  primaryHost,
}: {
  host: string;
  primaryHost?: string | null;
}): DomainScopeFlags {
  return scopeFlagsFromMode(
    shouldAutoMarkThirdParty({ host, primaryHost }) ? 'third_party' : 'none',
  );
}

export function hasGranularDomainConfigInput(
  entry: DomainConfigNormalizeInput,
): boolean {
  return (
    entry.isInFunctionalityScope != null ||
    entry.isInTestingScope != null ||
    entry.isThirdParty != null ||
    (entry.pathRules?.length ?? 0) > 0
  );
}

const isBlocklistScope = (scope: DomainPathRule['scope']): boolean =>
  scope === 'BLOCKLIST';

export const hasDomainPathRules = (
  pathRules: DomainPathRuleInput[] | undefined,
): boolean => (pathRules?.length ?? 0) > 0;

export const hasAllowlistPathRules = (
  pathRules: DomainPathRuleInput[] | undefined,
): boolean => (pathRules ?? []).some(isHostRestrictingAllowlistRule);

export const isBlocklistOnlyPartial = (
  pathRules: DomainPathRuleInput[] | undefined,
): boolean =>
  hasDomainPathRules(pathRules) && !hasAllowlistPathRules(pathRules);

export const domainScopeConflictsWithPathRules = (
  entry: Pick<
    DomainConfigNormalizeInput,
    'pathRules' | 'isInFunctionalityScope' | 'isInTestingScope'
  >,
): boolean =>
  hasAllowlistPathRules(entry.pathRules) && entry.isInTestingScope != null;

export const resolveDomainTestingScope = (
  entry: Pick<DomainConfigNormalizeInput, 'isInTestingScope'>,
): boolean => entry.isInTestingScope ?? false;

export const resolveDomainFunctionalityScope = (
  entry: Pick<
    DomainConfigNormalizeInput,
    'isInFunctionalityScope' | 'isInTestingScope'
  >,
): boolean =>
  resolveExclusiveDomainScopeFlags({
    isInFunctionalityScope: entry.isInFunctionalityScope,
    isInTestingScope: entry.isInTestingScope,
  }).isInFunctionalityScope;

export const deriveDomainScopeFromAllowlistRules = (
  pathRules: DomainPathRule[],
): {
  isInFunctionalityScope: boolean;
  isInTestingScope: boolean;
  isThirdParty: boolean;
} => {
  const allowlistRules = pathRules.filter(isHostRestrictingAllowlistRule);

  return resolveExclusiveDomainScopeFlags({
    isInFunctionalityScope: allowlistRules.some(
      (rule) => rule.isInFunctionalityScope,
    ),
    isInTestingScope: allowlistRules.some((rule) => rule.isInTestingScope),
    isThirdParty: false,
  });
};

export function normalizePathRules({
  pathRules,
  isInFunctionalityScope,
  isInTestingScope,
  isThirdParty,
}: {
  pathRules: DomainPathRuleInput[];
  isInFunctionalityScope: boolean;
  isInTestingScope: boolean;
  isThirdParty?: boolean;
}): DomainPathRule[] {
  const domainDefaults = resolveExclusiveDomainScopeFlags({
    isInFunctionalityScope,
    isInTestingScope,
    isThirdParty,
  });

  const dedupedByPath = new Map<string, DomainPathRuleInput>();

  for (const rule of pathRules) {
    dedupedByPath.set(rule.path, rule);
  }

  return [...dedupedByPath.values()].map((rule) => {
    if (isBlocklistScope(rule.scope)) {
      return {
        path: rule.path,
        scope: 'BLOCKLIST' as const,
        ...scopeFlagsFromMode('none'),
      };
    }

    const inheritHostScopeDefaults = !rule.isThirdParty;
    const resolved = resolveExclusiveDomainScopeFlags({
      isInTestingScope:
        rule.isInTestingScope ??
        (inheritHostScopeDefaults ? domainDefaults.isInTestingScope : false),
      isInFunctionalityScope:
        rule.isInFunctionalityScope ??
        (inheritHostScopeDefaults
          ? domainDefaults.isInFunctionalityScope
          : false),
      isThirdParty: rule.isThirdParty ?? domainDefaults.isThirdParty,
    });

    return {
      path: rule.path,
      scope: 'ALLOWLIST' as const,
      isInFunctionalityScope: resolved.isInFunctionalityScope,
      isInTestingScope: resolved.isInTestingScope,
      isThirdParty: resolved.isThirdParty,
    };
  });
}

function applyDefaultDomainScopeFields<T extends DomainConfigScopeSyncInput>(
  entry: T,
): T & {
  isInFunctionalityScope: boolean;
  isInTestingScope: boolean;
  isThirdParty: boolean;
  pathRules: DomainPathRule[];
} {
  return {
    ...entry,
    ...scopeFlagsFromMode('attack'),
    pathRules: [],
  };
}

export function normalizeDomainConfigInput<
  T extends DomainConfigNormalizeInput & Record<string, unknown>,
>(
  entry: T,
): T & {
  isInFunctionalityScope: boolean | null;
  isInTestingScope: boolean | null;
  isThirdParty: boolean | null;
  pathRules: DomainPathRule[];
} {
  const normalizedEntry = entry as T;

  if (domainScopeConflictsWithPathRules(normalizedEntry)) {
    throw new Error(
      'Domain-level testing scope cannot be set when allowlist path rules are configured',
    );
  }

  if (!hasGranularDomainConfigInput(normalizedEntry)) {
    return applyDefaultDomainScopeFields(normalizedEntry);
  }

  const pathRulesInput = normalizedEntry.pathRules ?? [];
  const hasAllowlist = hasAllowlistPathRules(pathRulesInput);
  const isPartial = hasDomainPathRules(pathRulesInput);
  const resolvedFlags = resolveExclusiveDomainScopeFlags({
    isInTestingScope: resolveDomainTestingScope(normalizedEntry),
    isInFunctionalityScope: resolveDomainFunctionalityScope(normalizedEntry),
    isThirdParty: normalizedEntry.isThirdParty,
  });
  const pathRules = normalizePathRules({
    pathRules: pathRulesInput,
    isInFunctionalityScope:
      isPartial && hasAllowlist ? false : resolvedFlags.isInFunctionalityScope,
    isInTestingScope:
      isPartial && hasAllowlist ? false : resolvedFlags.isInTestingScope,
    isThirdParty:
      isPartial && hasAllowlist ? false : resolvedFlags.isThirdParty,
  });

  if (isPartial && hasAllowlist) {
    const exclusiveDomainFlags = resolveExclusiveDomainScopeFlags({
      isInFunctionalityScope: normalizedEntry.isInFunctionalityScope,
      isInTestingScope: false,
      isThirdParty: normalizedEntry.isThirdParty,
    });

    return {
      ...normalizedEntry,
      isInFunctionalityScope: exclusiveDomainFlags.isThirdParty
        ? false
        : (normalizedEntry.isInFunctionalityScope ?? null),
      isInTestingScope: null,
      isThirdParty: exclusiveDomainFlags.isInFunctionalityScope
        ? false
        : exclusiveDomainFlags.isThirdParty
          ? true
          : (normalizedEntry.isThirdParty ?? null),
      pathRules,
    };
  }

  return {
    ...normalizedEntry,
    isInTestingScope: resolvedFlags.isInTestingScope,
    isInFunctionalityScope: resolvedFlags.isInFunctionalityScope,
    isThirdParty: resolvedFlags.isThirdParty,
    pathRules,
  };
}

export function normalizeStoredDomainConfig<
  T extends DomainConfigNormalizeInput & Record<string, unknown>,
>(
  entry: T,
  options: NormalizeStoredDomainConfigOptions,
): T & {
  isInFunctionalityScope: boolean | null;
  isInTestingScope: boolean | null;
  isThirdParty: boolean | null;
  pathRules: DomainPathRule[];
} {
  const sanitized = domainScopeConflictsWithPathRules(entry)
    ? {
        ...entry,
        isInTestingScope: null,
      }
    : entry;

  const withCatalogMark = shouldAutoMarkThirdParty({
    host: sanitized.domain,
    primaryHost: options.primaryHost,
    isInTestingScope: sanitized.isInTestingScope,
    isInFunctionalityScope: sanitized.isInFunctionalityScope,
    isThirdParty: sanitized.isThirdParty,
  })
    ? {
        ...sanitized,
        ...scopeFlagsFromMode('third_party'),
      }
    : isApplicationPrimaryHost({
          host: sanitized.domain,
          primaryHost: options.primaryHost,
        })
      ? {
          ...sanitized,
          isThirdParty: false,
          isInFunctionalityScope: sanitized.isThirdParty
            ? true
            : sanitized.isInFunctionalityScope,
        }
      : sanitized;

  return normalizeDomainConfigInput(withCatalogMark);
}

export function normalizeStoredDomainConfigList<
  T extends DomainConfigNormalizeInput & Record<string, unknown>,
>({
  entries,
  primaryHost,
}: {
  entries: T[];
  primaryHost: string;
}): Array<
  T & {
    isInFunctionalityScope: boolean | null;
    isInTestingScope: boolean | null;
    isThirdParty: boolean | null;
    pathRules: DomainPathRule[];
  }
> {
  return entries.map((entry) =>
    normalizeStoredDomainConfig(entry, { primaryHost }),
  );
}
